<template>
    <v-row class="mt-1">
        <v-col cols="6">
            <v-form v-model="isFormValid" @submit.prevent="submitNewTeacher">
                <v-card class="px-4 py-2">
                    <v-card-title>Add an individual teacher</v-card-title>
                    <v-card-text>
                        <label for="name" class="ma-0">Name</label>
                        <v-text-field
                            v-model="newTeacher.name"
                            :rules="[rules.required, rules.trimmed]"
                            variant="outlined"
                            density="compact"
                            name="name"
                        />
                        <label for="username">Email address (will be used as username)</label>
                        <v-text-field
                            v-model="newTeacher.email"
                            :rules="[rules.required, rules.trimmed, rules.email, rules.unique, rules.no_diacritics]"
                            density="compact"
                            validate-on="blur"
                            variant="outlined"
                            name="username"
                        />
                        <label for="password">Password</label>
                        <v-text-field
                            v-model="newTeacher.password"
                            density="compact"
                            :rules="[rules.required, rules.trimmed, rules.password_length]"
                            variant="outlined"
                            type="password"
                            name="password"
                            :autocomplete="false"
                        />
                        <label for="classes">Class(es)</label>
                        <v-select
                            v-model="newTeacher.classes"
                            variant="outlined"
                            density="compact"
                            chips
                            multiple
                            :items="classes"
                            item-title="name"
                            item-value="name"
                            name="classes"
                        ></v-select>
                        <label for="isAdmin">Admin</label>
                        <v-checkbox
                            v-model="newTeacher.admin"
                            label="Make the teacher an administrator for the school"
                            name="isAdmin"
                        />

                        <p>Note: An email with the login details will be automatically sent to the teacher.</p>
                    </v-card-text>

                    <v-card-actions>
                        <v-btn
                            :disabled="!isFormValid"
                            variant="flat"
                            type="submit"
                            :loading="addInProgress"
                            color="brand"
                            >Save</v-btn
                        >
                    </v-card-actions>
                </v-card>
            </v-form>
        </v-col>
        <v-col cols="6">
            <ParseUsers
                callerRole="admin"
                user-type="teacher"
                :addUsers="addUsers"
                @on-saved="router.push('/admin/view-teachers')"
            />
        </v-col>
    </v-row>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { useAdminStore } from '~/stores/admin';
import useSnackbar from '~/composables/useSnackbar';
import { NewTeacher, ParsedUser } from '@ilteducation/giglets-types';

definePage({ meta: { area: 'teachersnav' } });
const store = useAdminStore();
store.fetchClasses();
store.fetchTeachers();
store.fetchSchool();

const { teachers, classes, isLoaded } = storeToRefs(store);
const router = useRouter();

const isFormValid = ref(false);
const newTeacher = ref<NewTeacher>({
    name: '',
    username: '',
    password: '',
    email: '',
    admin: false,
    classes: [],
});
const addInProgress = ref(false);

const rules = {
    required: (value: string) => !!value || 'Required field.',
    email: (value: string) => /.+@.+\..+/.test(value) || 'Invalid email.',
    unique: (value: string) => {
        const teacher = teachers.value.find((t) => t.email === value);
        return !teacher || 'Email address is already used.';
    },
    trimmed: (value: string) => value.trim() === value || 'Cannot start or end with a space.',
    password_length: (value: string) => value.length >= 8 || `Password must be at least 8 characters long.`,
    no_diacritics: (value: string) => {
        const normalized = value
            .normalize('NFD') // decompose accented characters
            .replace(/[\u0300-\u036F]/g, '') // remove accents
            .replace(/[^A-Za-z0-9\-@_%#*.,!+\x20]/g, ''); // remove invalid characters
        return normalized === value || 'Only A-Z, a-z, 0-9, space, and the characters: -@_%#*.,!+ are allowed.';
    },
};

const submitNewTeacher = async () => {
    if (!isFormValid.value) {
        return;
    }

    try {
        addInProgress.value = true;

        newTeacher.value.username = newTeacher.value.email;
        await store.addTeacher(newTeacher.value);
        await router.push('/admin/view-teachers');
        useSnackbar().snackbarSuccess('Teacher added successfully.');
    } finally {
        addInProgress.value = false;
    }
};

const addUsers = async (type: 'pupil' | 'teacher', users: ParsedUser[]) => {
    await store.addUsers(type, users);
    isLoaded.value.classes = false;
};
</script>
